import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { logoImg } from '../../utils/images';
import '../../components/LoginSupabase/LoginS.scss';
import './ResetPasswordPage.scss';

export const ResetPasswordPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    const verifySession = async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (!mounted) return;

      if (error) {
        setError(error.message);
        setIsValidSession(false);
        return;
      }

      if (session) {
        setIsValidSession(true);
      } else {
        setError('Invalid or expired reset link. Please request a new one.');
        setIsValidSession(false);
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || session) {
        setIsValidSession(true);
        setError(null);
      }
    });

    verifySession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password:  password});
      if (error) throw error;
      setDone(true);
    } catch (err: any) {
      setError(err.message || 'Failed to update password. This link may have expired.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="resetPasswordPage">
      <div className="login">
        <div className="login__container">
          <div className="login__t">
            <div className="login__logo-wrapper">
              <img src={logoImg} alt="Dovery" className="login__logo" />
            </div>

            {done ? (
              <div className="auth-message auth-message--success">
                <p className="auth-message__text">
                  Your password has been updated successfully.
                </p>
                <button
                  type="button"
                  className="login-form__submit"
                  onClick={() => navigate('/')}
                >
                  Go to Login
                </button>
              </div>
            ) : !isValidSession ? (
              <div className="auth-message auth-message--waiting">
                <p className="auth-message__text">
                  {error || 'Verifying your reset link…'}
                </p>
                <p className="auth-message__hint">
                  If nothing happens, your link may have expired.{' '}
                  <button
                    type="button"
                    className="auth-message__link"
                    onClick={() => navigate('/forgot-password')}
                  >
                    Request a new one.
                  </button>
                </p>
              </div>
            ) : (
              <form className="login-form" onSubmit={handleSubmit}>
                <p className="auth-form__description">
                  Enter a new password for your account.
                </p>

                <div className="login-form__field">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="login-form__input login-form__input--with-toggle"
                    placeholder="New Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="login-form__password-toggle"
                    onClick={() => setShowPassword((current) => !current)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                <div className="login-form__field">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="login-form__input login-form__input--with-toggle"
                    placeholder="Confirm New Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="login-form__password-toggle"
                    onClick={() => setShowConfirmPassword((current) => !current)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    title={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                {error && <p className="login-form__error">{error}</p>}

                <button
                  type="submit"
                  className="login-form__submit"
                  disabled={isLoading}
                >
                  {isLoading ? 'Updating…' : 'Set New Password'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
